import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* ═══════════════════════════════════════════════════════════════
   THUNKS
═══════════════════════════════════════════════════════════════ */

export const fetchOffers = createAsyncThunk(
  "offer/fetchAll",
  async ({ page = 1, limit = 10, product = "", status = "", search = "" } = {}, thunkAPI) => {
    try {
      let url = `/api/offers?page=${page}&limit=${limit}&search=${search}`;
      if (product) url += `&product=${product}`;
      if (status !== "") url += `&status=${status}`;
      const res = await api.get(url);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to fetch offers");
    }
  }
);

export const fetchOfferById = createAsyncThunk(
  "offer/fetchById",
  async (id, thunkAPI) => {
    try {
      const res = await api.get(`/api/offers/${id}`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch offer");
    }
  }
);

export const createOffer = createAsyncThunk(
  "offer/create",
  async (data, thunkAPI) => {
    try {
      const res = await api.post("/api/offers", data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to create offer");
    }
  }
);

export const updateOffer = createAsyncThunk(
  "offer/update",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await api.put(`/api/offers/${id}`, data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to update offer");
    }
  }
);

export const toggleOfferStatus = createAsyncThunk(
  "offer/toggleStatus",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/api/offers/${id}/status`);
      return res.data.data;
    } catch {
      return thunkAPI.rejectWithValue("Failed to toggle offer status");
    }
  }
);

export const deleteOffer = createAsyncThunk(
  "offer/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/api/offers/${id}`);
      return id;
    } catch {
      return thunkAPI.rejectWithValue("Failed to delete offer");
    }
  }
);

/* ═══════════════════════════════════════════════════════════════
   SLICE
═══════════════════════════════════════════════════════════════ */
const offerSlice = createSlice({
  name: "offer",
  initialState: {
    loading:      false,
    error:        null,
    success:      false,
    offers:       [],
    pagination:   {},
    currentOffer: null,
  },
  reducers: {
    resetOfferState: (state) => {
      state.loading = false;
      state.error   = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ── fetchAll ── */
      .addCase(fetchOffers.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(fetchOffers.fulfilled, (state, action) => {
        state.loading    = false;
        state.offers     = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOffers.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── fetchById ── */
      .addCase(fetchOfferById.pending,   (state)         => { state.loading = true; state.currentOffer = null; })
      .addCase(fetchOfferById.fulfilled, (state, action) => { state.loading = false; state.currentOffer = action.payload; })
      .addCase(fetchOfferById.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── create ── */
      .addCase(createOffer.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(createOffer.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.offers  = [action.payload, ...state.offers];
      })
      .addCase(createOffer.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── update ── */
      .addCase(updateOffer.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(updateOffer.fulfilled, (state, action) => {
        state.loading       = false;
        state.success       = true;
        state.currentOffer  = action.payload;
        const idx = state.offers.findIndex((o) => o._id === action.payload._id);
        if (idx !== -1) state.offers[idx] = action.payload;
      })
      .addCase(updateOffer.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── toggleStatus ── */
      .addCase(toggleOfferStatus.fulfilled, (state, action) => {
        const idx = state.offers.findIndex((o) => o._id === action.payload._id);
        if (idx !== -1) state.offers[idx].status = action.payload.status;
      })

      /* ── delete ── */
      .addCase(deleteOffer.fulfilled, (state, action) => {
        state.offers = state.offers.filter((o) => o._id !== action.payload);
      });
  },
});

export const { resetOfferState } = offerSlice.actions;
export default offerSlice.reducer;